(() => {
    // Prevent multiple executions
    if (window.__customDeliveryMessageLoaded) return;
    window.__customDeliveryMessageLoaded = true;

    const WRAPPER_SELECTOR = '.custom-delivery-message-wraper';
    const CUTOFF_HOUR = 14;
    const TIMEZONE = 'Europe/London'; // London timezone for cutoff calculations

    // ========== DISPATCH CONFIGURATION ==========
    // Dispatch working days: Monday to Friday (1, 2, 3, 4, 5)
    // 0 = Sunday, 1 = Monday, 2 = Tuesday, 3 = Wednesday, 4 = Thursday, 5 = Friday, 6 = Saturday
    const DISPATCH_WORKING_DAYS = [1, 2, 3, 4, 5];

    // Dispatch delay in days (0.5 means same day dispatch)
    const DISPATCH_DELAY_DAYS = 0.5;
    // =================================================

    // ========== DELIVERY CONFIGURATION ==========
    // Delivery working days: Monday to Saturday (1, 2, 3, 4, 5, 6)
    // 0 = Sunday, 1 = Monday, 2 = Tuesday, 3 = Wednesday, 4 = Thursday, 5 = Friday, 6 = Saturday
    const DELIVERY_WORKING_DAYS = [1, 2, 3, 4, 5, 6];

    // Delivery delay in days (1 means 1 day after dispatch)
    const DELIVERY_DELAY_DAYS = 1;
    // =================================================

    // ========== HOLIDAYS CONFIGURATION ==========
    // UK bank holidays (England & Wales). Format: 'YYYY-MM-DD'.
    // Not counted as business days for BOTH dispatch and delivery.
    const HOLIDAYS = [
        // 2026
        '2026-01-01', // New Year's Day
        '2026-04-03', // Good Friday
        '2026-04-06', // Easter Monday
        '2026-05-04', // Early May bank holiday
        '2026-05-25', // Spring bank holiday
        '2026-08-31', // Summer bank holiday
        '2026-12-25', // Christmas Day
        '2026-12-28', // Boxing Day (substitute)
    ];
    // =================================================

    /**
     * Check if a date is a working day
     * @param {Date} date - The date to check
     * @param {Array} workingDays - Array of working day numbers (0-6)
     * @returns {boolean} - True if the date is a working day
     */
    const isWorkingDay = (date, workingDays = DISPATCH_WORKING_DAYS) => {
        const dayOfWeek = date.getDay();
        // Build YYYY-MM-DD from local date parts so it stays aligned with
        // the London wall-clock used to construct the Date (toISOString would
        // shift to UTC and miss holidays for non-UK browsers).
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;

        // Check if the day of week is in the working days array
        if (!workingDays.includes(dayOfWeek)) return false;

        // Check if holiday
        if (HOLIDAYS.includes(dateStr)) return false;

        return true;
    };

    /**
     * Add working days to a date
     * @param {Date} startDate - The starting date
     * @param {number} daysToAdd - Number of working days to add
     * @param {Array} workingDays - Array of working day numbers (0-6)
     * @returns {Date} - The calculated date
     */
    const addWorkingDays = (startDate, daysToAdd, workingDays = DISPATCH_WORKING_DAYS) => {
        const result = new Date(startDate);
        let remainingDays = daysToAdd;

        while (remainingDays > 0) {
            result.setDate(result.getDate() + 1);
            if (isWorkingDay(result, workingDays)) {
                remainingDays--;
            }
        }

        return result;
    };

    /**
     * Find the next working day starting from a given date
     * @param {Date} fromDate - The starting date
     * @param {Array} workingDays - Array of working day numbers (0-6)
     * @returns {Date} - The next working day
     */
    const getNextWorkingDay = (fromDate, workingDays = DISPATCH_WORKING_DAYS) => {
        const result = new Date(fromDate);
        result.setDate(result.getDate() + 1);

        while (!isWorkingDay(result, workingDays)) {
            result.setDate(result.getDate() + 1);
        }

        return result;
    };

    /**
     * Get the starting point for dispatch calculation
     * Rules:
     * - If current day is not a business day → use next business day
     * - If current day is a business day but after cutoff (14:00 London time) → use next business day
     * - If current day is a business day and before cutoff → use current day
     */
    const getDispatchStartDate = () => {
        // Get current time in London timezone
        const londonTime = new Date().toLocaleString('en-US', { timeZone: TIMEZONE });
        const now = new Date(londonTime);

        const currentHour = now.getHours();
        const currentMinute = now.getMinutes();

        // Check if today is a working day for dispatch
        const isTodayWorkingDay = isWorkingDay(now, DISPATCH_WORKING_DAYS);

        // If today is not a working day, find the next working day
        if (!isTodayWorkingDay) {
            return getNextWorkingDay(now, DISPATCH_WORKING_DAYS);
        }

        // Today is a working day - check cutoff time
        const isAfterCutoff = currentHour >= CUTOFF_HOUR;

        // If after cutoff, use next working day
        if (isAfterCutoff) {
            return getNextWorkingDay(now, DISPATCH_WORKING_DAYS);
        }

        // Before cutoff on a working day - use today
        return now;
    };

    /**
     * Format date as "Mon 01 Feb"
     */
    const formatDate = (date) => {
        const days = ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'];
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

        const dayName = days[date.getDay()];
        const day = String(date.getDate()).padStart(2, '0');
        const month = months[date.getMonth()];

        return `${dayName} ${day} ${month}`;
    };

    /**
     * Format date range optimizing month display
     * If both dates are in the same month, only show month on the second date
     * Example: "Thurs 05 - Mon 09 Feb" instead of "Thurs 05 Feb - Mon 09 Feb"
     * If different months: "Thurs 28 Feb - Mon 03 Mar"
     */
    const formatDateRange = (startDate, endDate) => {
        const days = ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'];
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

        const startDayName = days[startDate.getDay()];
        const startDay = String(startDate.getDate()).padStart(2, '0');
        const startMonth = months[startDate.getMonth()];

        const endDayName = days[endDate.getDay()];
        const endDay = String(endDate.getDate()).padStart(2, '0');
        const endMonth = months[endDate.getMonth()];

        // Check if both dates are in the same month and year
        const sameMonth = startDate.getMonth() === endDate.getMonth() &&
            startDate.getFullYear() === endDate.getFullYear();

        if (sameMonth) {
            // Same month: only show month on second date
            return `${startDayName} ${startDay} - ${endDayName} ${endDay} ${endMonth}`;
        } else {
            // Different months: show month on both dates
            return `${startDayName} ${startDay} ${startMonth} - ${endDayName} ${endDay} ${endMonth}`;
        }
    };

    /**
     * Calculate delivery date based on dispatch and delivery delays
     * @param {Date} dispatchDate - The dispatch date
     * @param {number} dispatchDelay - Dispatch delay in days (0.5 = same day)
     * @param {number} deliveryDelay - Delivery delay in days
     * @returns {Date} - The calculated delivery date
     */
    const calculateDeliveryDate = (dispatchDate, dispatchDelay, deliveryDelay) => {
        console.log('[Delivery Message] Calculating delivery date:', {
            dispatchDate,
            dispatchDelay,
            deliveryDelay
        });

        let result = new Date(dispatchDate);

        // Add dispatch delay (can be fractional like 0.5)
        const dispatchDelayDays = Math.floor(dispatchDelay);
        const dispatchDelayFraction = dispatchDelay - dispatchDelayDays;

        // Add whole days first
        if (dispatchDelayDays > 0) {
            result = addWorkingDays(result, dispatchDelayDays, DISPATCH_WORKING_DAYS);
        }

        // Add delivery delay (must be whole days)
        const totalDeliveryDays = Math.ceil(deliveryDelay);
        result = addWorkingDays(result, totalDeliveryDays, DELIVERY_WORKING_DAYS);

        console.log('[Delivery Message] Calculated delivery date:', result);
        return result;
    };

    /**
     * Get pickup availability message for specific providers
     */
    const getPickupMessage = (provider) => {
        const pickupLocations = {
            'London Soho': 'Pick up available in London Soho'
        };

        return pickupLocations[provider] || null;
    };

    /**
     * Calculate delivery message based on provider
     */
    const calculateDeliveryMessage = (provider) => {
        console.log('[Delivery Message] Calculating message for provider:', provider);

        const providerText = typeof provider === 'string' ? provider.trim() : '';
        const providerNormalized = providerText.toLowerCase();
        const includesAny = (values) => values.some((value) => providerNormalized.includes(value));

        if (!providerText) {
            console.log('[Delivery Message] No provider, using default message');
            return 'Order now to receive <strong>soon</strong>';
        }

        const dispatchStartDate = getDispatchStartDate();
        console.log('[Delivery Message] Dispatch start date:', dispatchStartDate);

        // Calculate delivery date using dispatch and delivery delays
        const deliveryDate = calculateDeliveryDate(
            dispatchStartDate,
            DISPATCH_DELAY_DAYS,
            DELIVERY_DELAY_DAYS
        );

        if (includesAny(['london soho', 'brighton 1', 'brighton 2'])) {
            // Next business day (1 day delivery)
            return `Order now to receive <strong>${formatDate(deliveryDate)}</strong>`;
        }

        if (includesAny(['drop 1'])) {
            // Next business day
            return `Order now to receive <strong>${formatDate(deliveryDate)}</strong>`;
        }

        if (includesAny(['zelph', 'drop 2'])) {
            // 3 to 5 business days
            const minDate = addWorkingDays(dispatchStartDate, 3, DELIVERY_WORKING_DAYS);
            const maxDate = addWorkingDays(dispatchStartDate, 5, DELIVERY_WORKING_DAYS);
            return `Order now to receive <strong>${formatDateRange(minDate, maxDate)}</strong>`;
        }

        if (includesAny(['drop 3'])) {
            // 7 to 10 business days
            const minDate = addWorkingDays(dispatchStartDate, 7, DELIVERY_WORKING_DAYS);
            const maxDate = addWorkingDays(dispatchStartDate, 10, DELIVERY_WORKING_DAYS);
            return `Order now to receive <strong>${formatDateRange(minDate, maxDate)}</strong>`;
        }

        return `Order now to receive <strong>soon</strong>`;
    };

    /**
     * Get current variant provider from product data
     */
    const getSelectedVariantProvider = () => {
        console.log('[Delivery Message] Getting selected variant provider...');

        // Try to get from selected variant input with data attribute
        // Look for any radio input that has data-variant-provider attribute and is checked
        const variantInput = document.querySelector('input[type="radio"][data-variant-provider]:checked');
        if (variantInput) {
            const provider = variantInput.getAttribute('data-variant-provider');
            console.log('[Delivery Message] Provider from checked input:', provider);
            if (provider) {
                return provider;
            }
        }

        // Fallback: get first available option if none is checked yet
        const firstInput = document.querySelector('input[type="radio"][data-variant-provider]');
        if (firstInput) {
            const provider = firstInput.getAttribute('data-variant-provider');
            console.log('[Delivery Message] Provider from first input:', provider);
            if (provider) {
                return provider;
            }
        }

        // Fallback: for products with only default variant (no variant picker rendered)
        // The provider is server-rendered as data-default-variant-provider on the product-info element
        const productInfoEl = document.querySelector('product-info[data-default-variant-provider]');
        if (productInfoEl) {
            const provider = productInfoEl.getAttribute('data-default-variant-provider');
            console.log('[Delivery Message] Provider from product-info (default variant):', provider);
            if (provider) {
                return provider;
            }
        }

        // Fallback: try window.product
        if (window.product && window.product.selected_or_first_available_variant) {
            const variant = window.product.selected_or_first_available_variant;
            const provider = variant?.metafields?.custom?.variant_location_provider || null;
            console.log('[Delivery Message] Provider from window.product:', provider);
            console.log('[Delivery Message] Variant data:', variant);
            return provider;
        }

        // Last fallback: search in window.product.variants
        if (!variantInput) {
            console.log('[Delivery Message] No variant input found');
            return null;
        }

        const variantId = parseInt(variantInput.value);
        console.log('[Delivery Message] Variant ID from input:', variantId);

        if (window.product && window.product.variants) {
            const variant = window.product.variants.find(v => v.id === variantId);
            const provider = variant?.metafields?.custom?.variant_location_provider || null;
            console.log('[Delivery Message] Provider from variants array:', provider);
            console.log('[Delivery Message] Variant data:', variant);
            return provider;
        }

        console.log('[Delivery Message] No provider found');
        return null;
    };

    /**
     * Update the delivery message in the DOM
     */
    const updateDeliveryMessage = () => {
        console.log('[Delivery Message] Updating delivery message...');

        const messageElement = document.querySelector('[data-custom-delivery-message="true"] .estimator-instock-message div:first-child');
        const pickupElement = document.querySelector('[data-custom-delivery-message="true"] .pickup-availability-message');

        if (!messageElement) {
            console.log('[Delivery Message] Message element not found');
            return;
        }

        const provider = getSelectedVariantProvider();
        const message = calculateDeliveryMessage(provider);
        const pickupMessage = getPickupMessage(provider);

        console.log('[Delivery Message] Calculated message:', message);
        messageElement.innerHTML = message;

        // Update or create pickup message
        if (pickupMessage) {
            if (pickupElement) {
                pickupElement.innerHTML = pickupMessage;
                pickupElement.style.display = 'block';
            } else {
                // Create pickup element if it doesn't exist
                const pickupMarkup = `<div class="pickup-availability-message" style="background: #f5f5f5; color: #303030; border-radius: 4px; padding: 6px 10px; margin: 4px 0px 0px; font-family: 'Inter'; font-weight: normal; font-size: 12px; line-height: 18px; text-align: center; display: block; width: 100%; box-sizing: border-box;">${pickupMessage}</div>`;
                messageElement.insertAdjacentHTML('afterend', pickupMarkup);
            }
        } else if (pickupElement) {
            pickupElement.style.display = 'none';
        }

        console.log('[Delivery Message] Message updated in DOM');
    };

    /**
     * Insert the message component into the DOM
     */
    const insertMessage = () => {
        console.log('[Delivery Message] insertMessage called');

        // Check if already inserted globally - remove any existing ones first
        const existingMessages = document.querySelectorAll('[data-custom-delivery-message="true"]');
        if (existingMessages.length > 0) {
            console.log('[Delivery Message] Found existing messages, removing:', existingMessages.length);
            existingMessages.forEach(msg => msg.remove());
        }

        const wrapper = document.querySelector(WRAPPER_SELECTOR);
        if (!wrapper) {
            console.log('[Delivery Message] Wrapper not found');
            return;
        }

        console.log('[Delivery Message] Wrapper found:', wrapper);

        const provider = getSelectedVariantProvider();
        const message = calculateDeliveryMessage(provider);
        const pickupMessage = getPickupMessage(provider);

        const markup = `
        <div data-custom-delivery-message="true" class="shopify-block shopify-app-block">
          <div class="custom-delivery-message-block">
            <div class="custom-delivery-message">
              <div class="estimator-instock-message" style="display: block;">
                <div style="background: #EE5C4F; color: #fff; border-radius: 4px; padding: 2px 10px; margin: 8px 0px 0px; font-family: 'Inter'; font-weight: normal; font-size: 12px; line-height: 18px; text-align: center; display: block; width: 100%; box-sizing: border-box;">
                  ${message}
                </div>
                ${pickupMessage ? `
                <div class="pickup-availability-message" style="background: #f5f5f5; color: #303030; border-radius: 4px; padding: 6px 10px; margin: 4px 0px 0px; font-family: 'Inter'; font-weight: normal; font-size: 12px; line-height: 18px; text-align: center; display: block; width: 100%; box-sizing: border-box;">
                  ${pickupMessage}
                </div>
                ` : ''}
              </div>
            </div>
          </div>
        </div>
      `.trim();

        wrapper.insertAdjacentHTML('beforeend', markup);
        console.log('[Delivery Message] Message inserted into DOM');
    };

    /**
     * Listen for variant changes
     */
    const setupVariantChangeListener = () => {
        console.log('[Delivery Message] Setting up variant change listeners...');

        // Listen for Shopify variant change events
        document.addEventListener('variant:change', () => {
            console.log('[Delivery Message] variant:change event detected');
            updateDeliveryMessage();
        });

        // Also listen for radio button changes (variant picker)
        document.addEventListener('change', (e) => {
            if (e.target.matches('input[type="radio"][data-variant-provider]')) {
                console.log('[Delivery Message] Input change detected:', e.target);
                console.log('[Delivery Message] Selected provider:', e.target.getAttribute('data-variant-provider'));
                setTimeout(updateDeliveryMessage, 100);
            }
        });

        console.log('[Delivery Message] Listeners set up successfully');
    };

    /**
     * Initialize
     */
    const init = () => {
        insertMessage();
        setupVariantChangeListener();
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init, { once: true });
    } else {
        init();
    }
})();
